import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { Modal, Box, Button, Typography, Stack, Avatar } from '@mui/material';
import { get } from '../utility/fetchWeb';
import VideoCalling from "../components/Layout/VideoCalling";
import AudioCall from '../components/Layout/AudioCall';
import { useFirebaseMessaging } from './FirebaseMessagingContext';
import toast from 'react-hot-toast';
import AgoraRTC from "agora-rtc-sdk-ng";

const IncomingCallContext = createContext();

export const IncomingCallProvider = ({ children }) => {
  const [incomingCall, setIncomingCall] = useState(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callConfig, setCallConfig] = useState(null);
  const [type, setType] = useState("");
  const ringtoneRef = useRef(null);
  const audioContextRef = useRef(null);
  const agoraClientRef = useRef(null);
  const callInstanceKeyRef = useRef(0);

  const messaging = useFirebaseMessaging();

  // Initialize audio on user interaction
  useEffect(() => {
    const initAudio = () => {
      if (!ringtoneRef.current) {
        const audio = new Audio('/ringtone.wav');
        audio.loop = true;
        audio.load();
        ringtoneRef.current = audio;
        
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        
        console.log('✅ Ringtone audio initialized');
      }
      
      document.removeEventListener('click', initAudio);
      document.removeEventListener('touchstart', initAudio);
    };

    document.addEventListener('click', initAudio, { once: true });
    document.addEventListener('touchstart', initAudio, { once: true });

    return () => {
      document.removeEventListener('click', initAudio);
      document.removeEventListener('touchstart', initAudio);
    };
  }, []);
  
  // Listen for service worker messages
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      console.log('📨 Service worker message:', event.data?.type);
      
      if (event.data?.type === 'INCOMING_CALL') {
        const callData = event.data.data;
        showIncomingCall({
          callerName: callData.callerName,
          callerId: callData.callerId,
          channel: callData.channelName,
          userType: callData.userType,
          type: callData.type
        });
      } else if (event.data?.type === 'ACCEPT_CALL') {
        handleAccept();
      } else if (event.data?.type === 'REJECT_CALL') {
        handleReject();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  // Subscribe to messaging events
  useEffect(() => {
    if (messaging?.onCallEvent) {
      const unsubscribe = messaging.onCallEvent((event) => {
        if (event.type === 'INCOMING_CALL' && event.data) {
          showIncomingCall({
            callerName: event.data.callerName,
            callerId: event.data.callerId,
            channel: event.data.channelName,
            userType: event.data.userType,
            type: event.data.type
          });
        }
      });
      return () => unsubscribe();
    }
  }, [messaging]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRingtone();
      cleanupAgoraClient();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const cleanupAgoraClient = async () => {
    if (agoraClientRef.current) {
      try {
        console.log('🧹 Cleaning up Agora client...');
        
        // Get all local tracks
        const localTracks = agoraClientRef.current.localTracks || [];
        
        // Stop and close all tracks
        localTracks.forEach(track => {
          if (track) {
            track.stop();
            track.close();
          }
        });
        
        // Unpublish if published
        if (agoraClientRef.current.channelName) {
          await agoraClientRef.current.unpublish().catch(() => {});
        }
        
        // Leave channel
        await agoraClientRef.current.leave().catch(() => {});
        
        // Clear reference
        agoraClientRef.current = null;
        
        console.log('✅ Agora client cleaned up');
      } catch (error) {
        console.error('❌ Error cleaning up Agora client:', error);
      }
    }
  };

  const playRingtone = async () => {
    try {
      if (audioContextRef.current?.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      if (ringtoneRef.current) {
        ringtoneRef.current.currentTime = 0;
        const playPromise = ringtoneRef.current.play();
        
        if (playPromise !== undefined) {
          await playPromise;
          console.log('✅ Ringtone playing');
        }
      }
    } catch (err) {
      console.warn('⚠️ Audio play failed:', err.message);
    }
  };

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
      console.log('🔇 Ringtone stopped');
    }
  };

  // Enhanced showIncomingCall function
  const showIncomingCall = async (callDetails) => {
    // Check if user is logged in
    const userId = sessionStorage.getItem("userId");
    if (!userId) {
      console.log('⚠️ User not logged in, ignoring incoming call');
      return;
    }

    // Check if already in a call
    if (isCallActive) {
      console.log('⚠️ Already in a call, cannot accept new call');
      return;
    }

    console.log('📞 Showing incoming call modal:', callDetails);
    setIncomingCall(callDetails);
    modeCheck(callDetails);
    
    await playRingtone();
  };

  const getAgoraDetails = async (channelName) => {
    try {
      console.log('🔑 Getting Agora token for channel:', channelName);
      
      const res = await get(
        `/posts/generate-new-token?channelName=${channelName || 'call'}&uid=${sessionStorage.getItem("userId") || 0}`
      );
      
      if (res && res.token) {
        console.log('✅ Agora token received');
        
        const config = {
          appId: res.appId || process.env.REACT_APP_AGORA_KEY,
          channelName: res.channel || res.channelName || channelName || "call",
          token: res.token
        };
        
        // Increment key to force new component instance
        callInstanceKeyRef.current += 1;
        
        setCallConfig(config);
        setIsCallActive(true);
        setIncomingCall(null);
        
        return true;
      } else {
        console.error("❌ Failed to fetch Agora token");
        toast.error('Failed to connect to call');
        return false;
      }
    } catch (error) {
      console.error("❌ Error fetching Agora token:", error);
      toast.error('Failed to connect to call');
      return false;
    }
  };

  const modeCheck = (details) => {
    if (details?.channel) {
      const parts = details.channel.split("-");
      const callType = parts[0].split("_")[1];
      setType(callType);
      console.log('📱 Call type detected:', callType);
    }
  };

  const handleCloseVideoChat = async () => {
    console.log('🔴 Ending call');
    stopRingtone();
    
    // Cleanup Agora client
    await cleanupAgoraClient();
    
    // Reset state
    setIsCallActive(false);
    setCallConfig(null);
    setType("");
    
    // Small delay to ensure cleanup completes
    await new Promise(resolve => setTimeout(resolve, 500));
    
    console.log('✅ Call cleanup complete, ready for next call');
  };

  const handleAccept = async () => {
    stopRingtone();
    
    if (incomingCall) {
      console.log('✅ Accepting call');
      
      const loadingToast = toast.loading('Connecting to call...');
      
      const success = await getAgoraDetails(incomingCall.channel);
      
      toast.dismiss(loadingToast);
      
      if (success) {
        toast.success('Call connected!');
      }
    }
  };

  const handleReject = () => {
    stopRingtone();
    console.log('❌ Rejecting call');
    setIncomingCall(null);
    setType("");
    toast('Call rejected', { icon: '📞' });
  };

  const handleEndCall = () => {
    console.log('🔴 Call ended by user');
    handleCloseVideoChat();
  };

  return (
    <IncomingCallContext.Provider value={{ 
      showIncomingCall, 
      handleEndCall,
      isCallActive, // Add this to let other components check call status
      incomingCall // Add this to let other components check if there's an incoming call
    }}>
      {children}
      
      {/* Incoming Call Modal */}
      {incomingCall && (
        <Modal 
          open={!!incomingCall}
          onClose={() => {}}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: { xs: '90%', sm: 400 },
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              p: 4,
              textAlign: 'center',
              animation: 'slideIn 0.3s ease-out',
              '@keyframes slideIn': {
                from: {
                  opacity: 0,
                  transform: 'translateY(-50px)'
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)'
                }
              }
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 3,
                position: 'relative'
              }}
            >
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: 'primary.main',
                  fontSize: '3rem',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      boxShadow: '0 0 0 0 rgba(46, 125, 50, 0.7)'
                    },
                    '50%': {
                      boxShadow: '0 0 0 20px rgba(46, 125, 50, 0)'
                    }
                  }
                }}
              >
                {incomingCall?.callerName?.charAt(0)?.toUpperCase() || '📞'}
              </Avatar>
            </Box>

            <Typography variant="h5" component="h2" mb={1} fontWeight="bold">
              Incoming {type === 'videocall' ? 'Video' : 'Voice'} Call
            </Typography>
            
            <Typography sx={{ mb: 1, color: 'text.secondary', fontSize: '1.1rem' }}>
              {incomingCall?.callerName || 'Unknown'}
            </Typography>
            
            <Typography sx={{ mb: 4, color: 'text.secondary', fontSize: '0.9rem' }}>
              {incomingCall?.userType || 'User'}
            </Typography>

            <Stack 
              direction="row" 
              spacing={3} 
              justifyContent="center"
            >
              <Button
                variant="contained"
                color="error"
                onClick={handleReject}
                sx={{
                  borderRadius: '50%',
                  width: 64,
                  height: 64,
                  minWidth: 'unset',
                  fontSize: '2rem',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    transition: 'transform 0.2s'
                  }
                }}
              >
                📞
              </Button>
              
              <Button
                variant="contained"
                color="success"
                onClick={handleAccept}
                sx={{
                  borderRadius: '50%',
                  width: 64,
                  height: 64,
                  minWidth: 'unset',
                  fontSize: '2rem',
                  animation: 'bounce 1s infinite',
                  '@keyframes bounce': {
                    '0%, 100%': {
                      transform: 'translateY(0)'
                    },
                    '50%': {
                      transform: 'translateY(-10px)'
                    }
                  },
                  '&:hover': {
                    transform: 'scale(1.1)',
                    animation: 'none'
                  }
                }}
              >
                📱
              </Button>
            </Stack>
          </Box>
        </Modal>
      )}

      {/* Active Call Component */}
      {isCallActive && callConfig && (
        type !== "videocall" ? (
          <AudioCall 
            key={`audio-call-${callInstanceKeyRef.current}`}
            appId={callConfig.appId}
            channel={callConfig.channelName}
            token={callConfig.token}
            uid={sessionStorage.getItem("userId") || 0}
            open={true}
            onClose={handleCloseVideoChat}
            startWithVideoOff={true}
          />
        ) : (
          <VideoCalling
            key={`video-call-${callInstanceKeyRef.current}`}
            appId={callConfig.appId}
            channel={callConfig.channelName}
            token={callConfig.token}
            uid={Number(sessionStorage.getItem("userId")) || 0}
            open={true}
            onClose={handleCloseVideoChat}
            startWithVideoOff={true}
            isAudio={type !== "videocall"}
          />
        )
      )}
    </IncomingCallContext.Provider>
  );
};

export const useIncomingCall = () => {
  const context = useContext(IncomingCallContext);
  if (!context) {
    throw new Error('useIncomingCall must be used within IncomingCallProvider');
  }
  return context;
};